// Downloads out of the browser editor: one clip (its stored file, or the
// clip as it sounds), a clip from Mine, and the whole room as a zip.
//
// "As it sounds" is the bounce's render (`renderSources`): the clip's own
// region with its warp, pitch and loop passes, dry — no lane fader, sends or
// master. The whole room is the seed page's zip — the mix, `arrangement`
// (every track rendered whole, bar 1 to the end) and `clips` (flat) — of
// what is on screen now, unsaved edits and takes held in the tab included.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Download, Loader2, Pause, Play } from "lucide-react";
import type { MyClip } from "@shared/api-types.js";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@web-shared/components/ui/dialog";
import { registerExternalPreview } from "@web-shared/lib/external-previews";
import type { ArrangementPlayerApi } from "@web-shared/hooks/useArrangementPlayer";
import type { Arrangement, Lane, LaneClip } from "@web-shared/lib/arrangement";
import { beatsToSeconds } from "@web-shared/lib/audio-engine";
import { clipSourcesFromStart } from "@web-shared/lib/player-sources";
import { encodeWav24 } from "@web-shared/lib/wav";
import { stemFiles } from "@/lib/stems";
import { cn } from "@web-shared/lib/utils";
import { cleanClipName, cleanTrackName } from "@/lib/track-name";
import {
  download,
  extOfKey,
  fetchBytes,
  keyUrl,
  recordDownload,
  recordSeedDownload,
  safeFilename,
  saveBytes,
  uniquePaths,
  type DownloadFile,
  type DownloadWhere,
} from "@/lib/download";
import { useAuthGate } from "../auth";

type ClipWhere = Extract<DownloadWhere, "materials" | "clip_editor">;

interface Options {
  arrangement: Arrangement | null;
  tempo: number;
  title: string;
  /** The seed this room edits; null for a room that is not a seed yet. */
  seedId: string | null;
  /** Where an object is fetched from, by key (the room's own resolver). */
  urlOfKey: (key: string) => string | undefined;
  player: Pick<ArrangementPlayerApi, "renderSources" | "renderMixdown">;
  /** The room's mixdown, as the Save renders it. */
  renderMaster: () => Promise<AudioBuffer>;
}

export interface RoomDownloads {
  /** Ask which form of this clip to download (signing in first if needed). */
  askClip: (clipId: string, where: ClipWhere) => void;
  /** Whether a clip has audio to download at all. */
  canDownloadClip: (clipId: string) => boolean;
  /** A clip from the producer's library: its file, as stored. */
  downloadMine: (clip: MyClip) => void;
  /** The whole room as a zip. */
  downloadSeed: (onProgress: (done: number, total: number) => void) => Promise<void>;
  /** The prompt; render it once. */
  dialog: ReactNode;
}

function findClip(arrangement: Arrangement | null, clipId: string): { lane: Lane; clip: LaneClip } | null {
  for (const lane of arrangement?.lanes ?? []) {
    const clip = lane.clips.find((c) => c.id === clipId) ?? lane.sessionClips.find((c) => c.id === clipId);
    if (clip) return { lane, clip };
  }
  return null;
}

/** Every lane's distinct objects, flat: `<title>/clips/<track> - <clip>.wav`. */
export function roomFiles(arrangement: Arrangement, title: string, urlOfKey: (key: string) => string | undefined): DownloadFile[] {
  const root = safeFilename(title);
  const files: DownloadFile[] = [];
  for (const lane of arrangement.lanes) {
    const track = safeFilename(cleanTrackName(lane.name));
    const seen = new Set<string>();
    for (const clip of [...lane.clips, ...lane.sessionClips]) {
      const key = clip.audioKey;
      if (!key || clip.ghost || seen.has(key)) continue;
      seen.add(key);
      const url = urlOfKey(key);
      if (!url) continue;
      const name = safeFilename(cleanClipName(clip.name) || track);
      files.push({ path: `${root}/clips/${name.toLowerCase() === track.toLowerCase() ? track : `${track} - ${name}`}${extOfKey(key)}`, url });
    }
  }
  return uniquePaths(files);
}

export function useRoomDownloads({ arrangement, tempo, title, seedId, urlOfKey, player, renderMaster }: Options): RoomDownloads {
  const gate = useAuthGate();
  const [asking, setAsking] = useState<{ clipId: string; where: ClipWhere } | null>(null);
  const askingRef = useRef(asking);
  askingRef.current = asking;
  const [busy, setBusy] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canDownloadClip = useCallback(
    (clipId: string) => {
      const found = findClip(arrangement, clipId);
      return !!found?.clip.audioKey && found.clip.kind !== "midi" && urlOfKey(found.clip.audioKey) !== undefined;
    },
    [arrangement, urlOfKey],
  );

  const askClip = useCallback(
    (clipId: string, where: ClipWhere) =>
      gate.requireSignIn("clip_download", "Sign in to download this clip", () => {
        setError(null);
        setAsking({ clipId, where });
      }),
    [gate],
  );

  const asked = asking ? findClip(arrangement, asking.clipId) : null;
  const askedName = asked ? cleanClipName(asked.clip.name) || cleanTrackName(asked.lane.name) : "";

  // Both forms' bytes, fetched or rendered once per prompt: the preview and
  // the download are the same bytes, so what you heard is what you get.
  const made = useRef<Partial<Record<Form, Promise<Uint8Array>>>>({});
  const audio = useRef<HTMLAudioElement | null>(null);
  const audioUrl = useRef<string | null>(null);
  const [previewing, setPreviewing] = useState<Form | null>(null);
  const [loading, setLoading] = useState<Form | null>(null);

  const stopPreview = useCallback(() => {
    audio.current?.pause();
    audio.current = null;
    if (audioUrl.current) URL.revokeObjectURL(audioUrl.current);
    audioUrl.current = null;
    setPreviewing(null);
  }, []);
  // A new prompt (or none) starts clean.
  useEffect(() => {
    made.current = {};
    setLoading(null);
    return stopPreview;
  }, [asking, stopPreview]);

  const bytesOf = (what: Form): Promise<Uint8Array> => {
    const held = made.current[what];
    if (held) return held;
    const key = asked?.clip.audioKey;
    const url = key ? urlOfKey(key) : undefined;
    if (!asked || !url) return Promise.reject(new Error("This clip has no audio to download."));
    const { clip, lane } = asked;
    const work =
      what === "source"
        ? fetchBytes(url)
        : player
            .renderSources(clipSourcesFromStart(lane, clip, url, tempo), beatsToSeconds(clip.duration, tempo))
            .then((rendered) => new Uint8Array(encodeWav24(rendered)));
    made.current[what] = work;
    // A failure is not kept: the next press tries again.
    work.catch(() => {
      if (made.current[what] === work) delete made.current[what];
    });
    return work;
  };

  const preview = (what: Form): void => {
    if (previewing === what) return stopPreview();
    stopPreview();
    const prompt = asking;
    setLoading(what);
    setError(null);
    void bytesOf(what)
      .then((bytes) => {
        if (askingRef.current !== prompt) return;
        const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: what === "rendered" ? "audio/wav" : mimeOfKey(asked?.clip.audioKey ?? "") }));
        const el = new Audio(url);
        audio.current = el;
        audioUrl.current = url;
        // The room's Stop and Space reach this preview too.
        const unregister = registerExternalPreview(stopPreview);
        el.onended = () => {
          unregister();
          setPreviewing((p) => (p === what ? null : p));
        };
        setPreviewing(what);
        return el.play();
      })
      .catch((err: unknown) => {
        stopPreview();
        setError(err instanceof Error ? err.message : "Couldn't play that.");
      })
      .finally(() => setLoading((l) => (l === what ? null : l)));
  };

  const run = (what: Form): void => {
    if (!asking || !asked || busy) return;
    const key = asked.clip.audioKey ?? "";
    const name = safeFilename(askedName);
    const prompt = asking;
    setBusy(what);
    setError(null);
    void bytesOf(what)
      .then((bytes) => {
        if (what === "source") saveBytes(`${name}${extOfKey(key)}`, bytes, mimeOfKey(key));
        else saveBytes(`${name} (as it sounds).wav`, bytes, "audio/wav");
        recordDownload("clip_download", `${prompt.where}|${what}`, seedId ?? undefined);
        setAsking(null);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "The download failed."))
      .finally(() => setBusy(null));
  };

  const downloadMine = useCallback(
    (clip: MyClip) =>
      gate.requireSignIn("clip_download", "Sign in to download this clip", () => {
        // Through the object proxy, which opens a library clip to its owner.
        const url = keyUrl(clip.audioKey);
        const name = safeFilename(clip.name.replace(/\.[a-z0-9]{2,5}$/i, ""));
        void download(name, [{ path: `${name}${extOfKey(clip.audioKey)}`, url }])
          .then(() => recordDownload("clip_download", "mine|source"))
          .catch(() => {});
      }),
    [gate],
  );

  const { renderMixdown } = player;
  const downloadSeed = useCallback(
    async (onProgress: (done: number, total: number) => void): Promise<void> => {
      if (!arrangement) throw new Error("Nothing to download yet.");
      const clips = roomFiles(arrangement, title, urlOfKey);
      const root = safeFilename(title);
      // Lanes with something on the timeline: a pads-only lane has no
      // arrangement to render.
      const lanes = arrangement.lanes.filter((l) => l.clips.length > 0).map((l) => ({ id: l.id, name: cleanTrackName(l.name) }));
      if (clips.length === 0 && lanes.length === 0) throw new Error("This room has no audio to download yet.");
      const files: DownloadFile[] = [];
      // The mix is a nicety: a render that fails must not cost the files.
      try {
        const mix = await renderMaster();
        if (mix.length > 0) files.push({ path: `${root}/${root} (mix).wav`, bytes: new Uint8Array(encodeWav24(mix)) });
      } catch {
        // no mix
      }
      // A stem ignores the listening mute/solo, as the mix above does.
      const stems = await stemFiles(
        lanes,
        (laneId) => renderMixdown(undefined, (id) => id === laneId, false),
        beatsToSeconds(arrangement.length, tempo),
        `${root}/arrangement`,
        onProgress,
      );
      const saved = await download(title, [...files, ...stems, ...clips], onProgress);
      recordSeedDownload(seedId ?? undefined, "editor", saved);
    },
    [arrangement, title, urlOfKey, renderMaster, renderMixdown, tempo, seedId],
  );

  const dialog = (
    <Dialog
      open={asking !== null}
      onOpenChange={(open) => {
        if (!open && !busy) setAsking(null);
      }}
    >
      <DialogContent className="w-[calc(100%-2rem)] max-w-xs">
        <DialogTitle className="truncate pr-6 text-sm font-semibold">Download “{askedName || "clip"}”</DialogTitle>
        <DialogDescription className="sr-only">Preview and download the source file, or the clip as it sounds.</DialogDescription>
        <div className="flex flex-col gap-2">
          {FORMS.map(({ what, title }) => (
            <div key={what} className="flex items-center gap-2 rounded-xl border border-panelline bg-panel p-2">
              <button
                type="button"
                onClick={() => preview(what)}
                disabled={loading !== null && loading !== what}
                aria-label={previewing === what ? `Stop ${title}` : `Preview ${title}`}
                title={previewing === what ? "Stop" : "Preview"}
                className={cn(
                  "flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors disabled:cursor-default disabled:opacity-50",
                  previewing === what ? "border-primary/40 bg-primary/15 text-primary" : "border-white/10 bg-white/5 text-foreground/80 hover:bg-white/10",
                )}
              >
                {loading === what ? <Loader2 className="size-3.5 animate-spin" /> : previewing === what ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
              </button>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</span>
              <button
                type="button"
                onClick={() => run(what)}
                disabled={busy !== null}
                aria-label={`Download ${title}`}
                title="Download"
                className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-default disabled:opacity-50"
              >
                {busy === what ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              </button>
            </div>
          ))}
        </div>
        {error && <p className="text-xs text-removed">{error}</p>}
      </DialogContent>
    </Dialog>
  );

  return { askClip, canDownloadClip, downloadMine, downloadSeed, dialog };
}

type Form = "source" | "rendered";
const FORMS: Array<{ what: Form; title: string }> = [
  { what: "source", title: "Source file" },
  { what: "rendered", title: "As it sounds" },
];

function mimeOfKey(key: string): string {
  const ext = extOfKey(key);
  return ext === ".mp3" ? "audio/mpeg" : ext === ".flac" ? "audio/flac" : ext === ".aif" || ext === ".aiff" ? "audio/aiff" : "audio/wav";
}
